import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Assignment as AssignIcon,
  Undo as ReturnIcon,
  SwapHoriz as TransferIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { useInventory } from '../contexts/InventoryContext';
import { useJob } from '../contexts/JobContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import { barcodeApi } from '../services/api.service';
import equipmentService from '../services/equipment.service';
import { getStatusDisplay, getStatusChipSx, getStatusChipVariant } from '../constants/equipmentStatus';
import BarcodeScanner from './BarcodeScanner';
import StatusHistoryDialog from './StatusHistoryDialog';
import UnplannedEquipmentDialog from './UnplannedEquipmentDialog';

export default function EquipmentStatusAndActions() {
  const { loading, error, clearError } = useInventory();
  const { jobList } = useJob();
  const { 
    selectedJobId, setSelectedJobId,
    selectedSiteId, setSelectedSiteId,
    sites, 
    handleBarcodeScan
  } = useInventoryPage();
  
  // Scanner and equipment state
  const [scannedItems, setScannedItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [scanError, setScanError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [manualEquipmentId, setManualEquipmentId] = useState('');
  
  // Fallback lookup state
  const [showFallbackLookup, setShowFallbackLookup] = useState(false);
  const [fallbackData, setFallbackData] = useState({
    manufacturer: '',
    model: '',
    serial_number: ''
  });
  const [fallbackResults, setFallbackResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Action form state
  const [selectedAction, setSelectedAction] = useState('');
  const [actionData, setActionData] = useState({
    jobId: '',
    siteId: '',
    notes: '',
    warehouse_id: '',
    rack_id: '',
    bay_id: ''
  });
  
  // History dialog state
  const [showHistory, setShowHistory] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  
  // Bulk actions state
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Unplanned equipment dialog state
  const [showUnplannedDialog, setShowUnplannedDialog] = useState(false);
  const [unplannedEquipmentData, setUnplannedEquipmentData] = useState(null);

  // Handle successful barcode scan
  const handleScanSuccess = async (scannedCode) => {
    try {
      setScanError(null);
      setIsProcessing(true);
      
      console.log('Scanned code:', scannedCode);
      
      // Look up equipment by barcode/ID
      const response = await barcodeApi.lookupByBarcode(scannedCode);
      console.log('Lookup response:', response);
      
      if (response.data) {
        const item = response.data;
        
        // Check if item is already in scanned list
        const existingIndex = scannedItems.findIndex(
          existing => existing.id === item.id || existing.equipment_id === item.equipment_id
        );
        
        if (existingIndex >= 0) {
          // Update existing item
          setScannedItems(prev => 
            prev.map((existing, index) => 
              index === existingIndex ? item : existing
            )
          );
        } else {
          // Add new item
          setScannedItems(prev => [...prev, item]);
        }
        
        // Set as current item for detailed view
        setCurrentItem(item);
        
        // Clear any previous action selection when scanning new item
        setSelectedAction('');
        setActionData({
          jobId: '',
          siteId: '',
          notes: '',
          warehouse_id: '',
          rack_id: '',
          bay_id: ''
        });
        
        // Clear manual input field
        setManualEquipmentId('');
        
      } else {
        setScanError('Equipment not found with QR code. Try using the fallback lookup below.');
      }
    } catch (error) {
      console.error('Error looking up equipment:', error);
      setScanError('Error looking up equipment. Try using the fallback lookup below.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle fallback lookup by manufacturer/model/serial
  const handleFallbackSearch = async () => {
    if (!fallbackData.manufacturer || !fallbackData.model) {
      setScanError('Please enter at least manufacturer and model for fallback lookup.');
      return;
    }

    try {
      setScanError(null);
      setIsSearching(true);
      
      // Search equipment by manufacturer, model, and optionally serial number
      const searchParams = {
        manufacturer: fallbackData.manufacturer.trim(),
        model: fallbackData.model.trim(),
        ...(fallbackData.serial_number.trim() && { serial_number: fallbackData.serial_number.trim() })
      };
      
      console.log('Fallback search params:', searchParams);
      
      // Use equipment service to search by details
      const response = await equipmentService.searchEquipment(searchParams);
      console.log('Fallback search response:', response);
      
      if (response.data && response.data.length > 0) {
        setFallbackResults(response.data);
        if (response.data.length === 1) {
          // Auto-select if only one match
          handleSelectFallbackItem(response.data[0]);
        }
      } else {
        setScanError(`No equipment found matching ${fallbackData.manufacturer} ${fallbackData.model}${fallbackData.serial_number ? ' (Serial: ' + fallbackData.serial_number + ')' : ''}`);
        setFallbackResults([]);
      }
    } catch (error) {
      console.error('Error in fallback search:', error);
      setScanError('Error searching equipment. Please try again.');
      setFallbackResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle selecting an item from fallback results
  const handleSelectFallbackItem = (item) => {
    // Same logic as successful scan
    const existingIndex = scannedItems.findIndex(
      existing => existing.id === item.id || existing.equipment_id === item.equipment_id
    );
    
    if (existingIndex >= 0) {
      setScannedItems(prev => 
        prev.map((existing, index) => 
          index === existingIndex ? item : existing
        )
      );
    } else {
      setScannedItems(prev => [...prev, item]);
    }
    
    setCurrentItem(item);
    setSelectedAction('');
    setActionData({
      jobId: '',
      siteId: '',
      notes: '',
      warehouse_id: '',
      rack_id: '',
      bay_id: ''
    });
    
    // Close fallback dialog
    setShowFallbackLookup(false);
    setFallbackResults([]);
    setFallbackData({ manufacturer: '', model: '', serial_number: '' });
  };

  // Handle QR code reprint
  const handleReprintQRCode = async (item) => {
    try {
      // Import QRCodeGenerator component functionality
      const qrData = {
        id: item.id || item.equipment_id,
        description: item.description || item.equipment_name,
        manufacturer: item.manufacturer,
        model: item.model,
        serial_number: item.serial_number
      };
      
      // For now, just show an alert - in production this would trigger actual printing
      alert(`QR Code reprint requested for:\n${item.description || item.equipment_name}\nID: ${item.id || item.equipment_id}\nSerial: ${item.serial_number || 'N/A'}`);
      
      // TODO: Implement actual QR code printing integration
      console.log('QR Code reprint data:', qrData);
      
    } catch (error) {
      console.error('Error reprinting QR code:', error);
      setScanError('Error reprinting QR code. Please try again.');
    }
  };

  // Get available actions based on equipment status
  const getAvailableActions = (item) => {
    if (!item) return [];
    
    console.log('Getting actions for item:', item);
    console.log('Item status:', item.status);
    console.log('Item status type:', typeof item.status);
    console.log('Item status lowercase:', item.status?.toLowerCase());
    
    const actions = [];
    
    // Normalize status to lowercase for comparison
    const normalizedStatus = item.status?.toLowerCase()?.trim();
    
    switch (normalizedStatus) {
      case 'available':
        actions.push({
          id: 'assign',
          label: 'Assign to Job',
          icon: AssignIcon,
          color: 'primary',
          variant: 'contained'
        });
        break;
        
      case 'assigned':
        actions.push(
          {
            id: 'return',
            label: 'Return to Inventory',
            icon: ReturnIcon,
            color: 'secondary',
            variant: 'contained'
          },
          {
            id: 'transfer',
            label: 'Transfer to Another Job',
            icon: TransferIcon,
            color: 'warning',
            variant: 'outlined'
          }
        );
        break;
        
      default:
        // For other statuses (maintenance, archived), show limited actions
        if (normalizedStatus === 'maintenance') {
          actions.push({
            id: 'return',
            label: 'Return from Maintenance',
            icon: ReturnIcon,
            color: 'secondary',
            variant: 'contained'
          });
        }
        break;
    }
    
    // Always show history action
    actions.push({
      id: 'history',
      label: 'View History',
      icon: HistoryIcon,
      color: 'info',
      variant: 'outlined'
    });
    
    console.log('Returning actions:', actions.map(a => a.id).join(', '));
    return actions;
  };

  // Handle action selection
  const handleActionSelect = (actionId) => {
    setSelectedAction(actionId);
    
    if (actionId === 'history') {
      setHistoryItem(currentItem);
      setShowHistory(true);
      setSelectedAction(''); // Reset selection
    }
  };

  // Handle action execution
  const handleExecuteAction = async () => {
    if (!currentItem || !selectedAction) return;
    
    try {
      setIsProcessing(true);
      
      switch (selectedAction) {
        case 'assign':
          await equipmentService.assignEquipment(actionData.jobId, {
            equipment_id: currentItem.id || currentItem.equipment_id,
            site_id: actionData.siteId,
            notes: actionData.notes
          });
          break;
          
        case 'return':
          await equipmentService.returnEquipment(currentItem.id || currentItem.equipment_id, {
            warehouse_id: actionData.warehouse_id,
            rack_id: actionData.rack_id,
            bay_id: actionData.bay_id,
            notes: actionData.notes
          });
          break;
          
        case 'transfer':
          await equipmentService.transferEquipment(currentItem.id || currentItem.equipment_id, {
            to_job_id: actionData.jobId,
            site_id: actionData.siteId,
            notes: actionData.notes
          });
          break;
      }
      
      // Refresh item data after action
      const refreshedResponse = await barcodeApi.lookupByBarcode(
        currentItem.barcode || currentItem.equipment_id || currentItem.id
      );
      
      if (refreshedResponse.data) {
        setCurrentItem(refreshedResponse.data);
        // Update in scanned items list
        setScannedItems(prev => 
          prev.map(item => 
            (item.id === currentItem.id || item.equipment_id === currentItem.equipment_id) 
              ? refreshedResponse.data 
              : item
          )
        );
      }
      
      // Reset action form
      setSelectedAction('');
      setActionData({
        jobId: '',
        siteId: '',
        notes: '',
        warehouse_id: '',
        rack_id: '',
        bay_id: ''
      });
      
    } catch (error) {
      console.error('Error executing action:', error);
      
      // Handle unplanned equipment assignment
      if (error.code === 'UNPLANNED_EQUIPMENT' && selectedAction === 'assign') {
        setUnplannedEquipmentData({
          equipment: currentItem,
          job: jobList?.find(j => j.id === actionData.jobId),
          originalAssignData: {
            equipment_id: currentItem.id || currentItem.equipment_id,
            site_id: actionData.siteId,
            notes: actionData.notes
          }
        });
        setShowUnplannedDialog(true);
      } else {
        setScanError(`Failed to ${selectedAction} equipment. Please try again.`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear current item
  const handleClearCurrent = () => {
    setCurrentItem(null);
    setSelectedAction('');
    setActionData({
      jobId: '',
      siteId: '',
      notes: '',
      warehouse_id: '',
      rack_id: '',
      bay_id: ''
    });
  };

  // Clear all scanned items
  const handleClearAll = () => {
    setScannedItems([]);
    setCurrentItem(null);
    setSelectedAction('');
    setActionData({
      jobId: '',
      siteId: '',
      notes: '',
      warehouse_id: '',
      rack_id: '',
      bay_id: ''
    });
  };

  // Handle unplanned equipment dialog confirmation
  const handleUnplannedEquipmentConfirm = async (addToPlan) => {
    if (!unplannedEquipmentData) return;
    
    try {
      setIsProcessing(true);
      
      // Proceed with assignment with forceAssign flag
      await equipmentService.assignEquipment(actionData.jobId, {
        ...unplannedEquipmentData.originalAssignData,
        forceAssign: true // This tells the service to bypass the planned check
      });
      
      // Refresh item data after action
      const refreshedResponse = await barcodeApi.lookupByBarcode(
        currentItem.barcode || currentItem.equipment_id || currentItem.id
      );
      
      if (refreshedResponse.data) {
        setCurrentItem(refreshedResponse.data);
        // Update in scanned items list
        setScannedItems(prev => 
          prev.map(item => 
            (item.id === currentItem.id || item.equipment_id === currentItem.equipment_id) 
              ? refreshedResponse.data 
              : item
          )
        );
      }
      
      // Reset action form
      setSelectedAction('');
      setActionData({
        jobId: '',
        siteId: '',
        notes: '',
        warehouse_id: '',
        rack_id: '',
        bay_id: ''
      });
      
      // Close dialog
      setShowUnplannedDialog(false);
      setUnplannedEquipmentData(null);
      
    } catch (error) {
      console.error('Error confirming unplanned equipment assignment:', error);
      setScanError('Failed to assign equipment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle unplanned equipment dialog cancellation
  const handleUnplannedEquipmentCancel = () => {
    setShowUnplannedDialog(false);
    setUnplannedEquipmentData(null);
    setSelectedAction('');
  };

  // Status color function now imported from centralized constants

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Equipment Status & Actions
      </Typography>

      {/* Error Display */}
      {(error || scanError) && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => {
            clearError();
            setScanError(null);
          }}
        >
          {error || scanError}
        </Alert>
      )}

      {/* Barcode Scanner - Always at top */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <BarcodeScanner 
          onScanSuccess={handleScanSuccess}
          onError={(err) => setScanError('Scanner error: ' + err)}
        />
        
        {/* Manual Equipment ID Input for Testing */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Alternative Lookup Methods
          </Typography>
          
          {/* Manual Equipment ID */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <TextField
              label="Equipment ID"
              size="small"
              value={manualEquipmentId}
              onChange={(e) => setManualEquipmentId(e.target.value)}
              placeholder="Enter equipment ID (e.g., 222)"
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              onClick={() => handleScanSuccess(manualEquipmentId)}
              disabled={!manualEquipmentId.trim() || isProcessing}
            >
              Look Up
            </Button>
          </Box>
          
          {/* Fallback Lookup Button */}
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setShowFallbackLookup(true)}
            disabled={isProcessing}
            fullWidth
          >
            Search by Manufacturer, Model & Serial Number
          </Button>
        </Box>
        
        {isProcessing && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">Looking up equipment...</Typography>
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        {/* Current Item Details */}
        {currentItem && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Current Equipment</Typography>
                <IconButton onClick={handleClearCurrent} size="small">
                  <ClearIcon />
                </IconButton>
              </Box>
              
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {currentItem.description || currentItem.equipment_name || 'Equipment Item'}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getStatusDisplay(currentItem.status).label}
                      sx={{
                        ...getStatusChipSx(currentItem.status, getStatusChipVariant(currentItem.status)),
                        mr: 1
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    <strong>ID:</strong> {currentItem.equipment_id || currentItem.id}
                  </Typography>
                  
                  {currentItem.serial_number && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Serial:</strong> {currentItem.serial_number}
                    </Typography>
                  )}
                  
                  {currentItem.manufacturer && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Make/Model:</strong> {currentItem.manufacturer} {currentItem.model || ''}
                    </Typography>
                  )}
                  
                  {currentItem.current_job_id && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Assigned to:</strong> Job {currentItem.current_job_id}
                    </Typography>
                  )}
                  
                  {currentItem.warehouse && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Location:</strong> {currentItem.warehouse} - {currentItem.rack || 'No rack'} - {currentItem.bay || 'No bay'}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Available Actions */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Available Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {getAvailableActions(currentItem).map((action) => {
                    const IconComponent = action.icon;
                    return (
                      <Button
                        key={action.id}
                        variant={action.variant}
                        color={action.color}
                        startIcon={<IconComponent />}
                        onClick={() => handleActionSelect(action.id)}
                        fullWidth
                      >
                        {action.label}
                      </Button>
                    );
                  })}
                  
                  {/* QR Code Reprint Button */}
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<PrintIcon />}
                    onClick={() => handleReprintQRCode(currentItem)}
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Reprint QR Code
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Action Form */}
        {selectedAction && selectedAction !== 'history' && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {selectedAction === 'assign' && 'Assign to Job'}
                {selectedAction === 'transfer' && 'Transfer to Job'}
                {selectedAction === 'return' && 'Return to Inventory'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {(selectedAction === 'assign' || selectedAction === 'transfer') && (
                  <>
                    <FormControl fullWidth required>
                      <InputLabel>Job</InputLabel>
                      <Select
                        value={actionData.jobId}
                        onChange={(e) => setActionData(prev => ({ ...prev, jobId: e.target.value }))}
                        label="Job"
                      >
                        {jobList?.map((job) => (
                          <MenuItem key={job.id} value={job.id}>
                            {job.name} ({job.job_number || job.id})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Site (Optional)</InputLabel>
                      <Select
                        value={actionData.siteId}
                        onChange={(e) => setActionData(prev => ({ ...prev, siteId: e.target.value }))}
                        label="Site (Optional)"
                      >
                        <MenuItem value="">
                          <em>No specific site</em>
                        </MenuItem>
                        {sites?.map((site) => (
                          <MenuItem key={site.id} value={site.id}>
                            {site.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {selectedAction === 'return' && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Warehouse</InputLabel>
                      <Select
                        value={actionData.warehouse_id}
                        onChange={(e) => setActionData(prev => ({ ...prev, warehouse_id: e.target.value }))}
                        label="Warehouse"
                      >
                        <MenuItem value="">
                          <em>Select warehouse</em>
                        </MenuItem>
                        {/* Warehouse options would come from context */}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Rack"
                      value={actionData.rack_id}
                      onChange={(e) => setActionData(prev => ({ ...prev, rack_id: e.target.value }))}
                      fullWidth
                    />

                    <TextField
                      label="Bay"
                      value={actionData.bay_id}
                      onChange={(e) => setActionData(prev => ({ ...prev, bay_id: e.target.value }))}
                      fullWidth
                    />
                  </>
                )}

                <TextField
                  label="Notes (Optional)"
                  value={actionData.notes}
                  onChange={(e) => setActionData(prev => ({ ...prev, notes: e.target.value }))}
                  multiline
                  rows={2}
                  fullWidth
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedAction('')}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleExecuteAction}
                    disabled={
                      isProcessing ||
                      (selectedAction === 'assign' && !actionData.jobId) ||
                      (selectedAction === 'transfer' && !actionData.jobId)
                    }
                    sx={{ flex: 1 }}
                  >
                    {isProcessing ? 'Processing...' : `${selectedAction === 'assign' ? 'Assign' : selectedAction === 'transfer' ? 'Transfer' : 'Return'}`}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Scanned Items List */}
        {scannedItems.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Scanned Items ({scannedItems.length})
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setShowBulkActions(!showBulkActions)}
                    startIcon={showBulkActions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mr: 1 }}
                  >
                    Bulk Actions
                  </Button>
                  <Button variant="outlined" onClick={handleClearAll}>
                    Clear All
                  </Button>
                </Box>
              </Box>

              <Collapse in={showBulkActions}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Bulk actions coming soon! For now, select items individually.
                </Alert>
              </Collapse>

              <List>
                {scannedItems.map((item, index) => (
                  <ListItem 
                    key={item.id || item.equipment_id || index}
                    button
                    onClick={() => setCurrentItem(item)}
                    selected={currentItem?.id === item.id || currentItem?.equipment_id === item.equipment_id}
                  >
                    <ListItemText
                      primary={item.description || item.equipment_name || 'Equipment Item'}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            ID: {item.equipment_id || item.id} •
                            Status: <Chip
                              label={getStatusDisplay(item.status).label}
                              sx={{
                                ...getStatusChipSx(item.status, getStatusChipVariant(item.status)),
                                ml: 0.5
                              }}
                              size="small"
                            />
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Status History Dialog */}
      {historyItem && (
        <StatusHistoryDialog
          open={showHistory}
          onClose={() => {
            setShowHistory(false);
            setHistoryItem(null);
          }}
          equipment={historyItem}
          onEquipmentUpdate={(updatedEquipment) => {
            // Update the equipment in the scanned items list
            setScannedItems(prev =>
              prev.map(item =>
                item.id === updatedEquipment.id ? updatedEquipment : item
              )
            );
          }}
        />
      )}
      
      {/* Fallback Lookup Dialog */}
      <Dialog 
        open={showFallbackLookup} 
        onClose={() => setShowFallbackLookup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon />
            Search Equipment by Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use this when QR code scanning fails. Enter manufacturer and model (required), 
            plus serial number if available for more precise matching.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Manufacturer *"
              value={fallbackData.manufacturer}
              onChange={(e) => setFallbackData(prev => ({ ...prev, manufacturer: e.target.value }))}
              placeholder="e.g., Caterpillar, John Deere"
              fullWidth
              required
            />
            <TextField
              label="Model *"
              value={fallbackData.model}
              onChange={(e) => setFallbackData(prev => ({ ...prev, model: e.target.value }))}
              placeholder="e.g., 320D, X590"
              fullWidth
              required
            />
            <TextField
              label="Serial Number"
              value={fallbackData.serial_number}
              onChange={(e) => setFallbackData(prev => ({ ...prev, serial_number: e.target.value }))}
              placeholder="Enter serial number for exact match (optional)"
              fullWidth
            />
          </Box>
          
          {/* Search Results */}
          {fallbackResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Found {fallbackResults.length} matching item{fallbackResults.length > 1 ? 's' : ''}
              </Typography>
              <List>
                {fallbackResults.map((item, index) => (
                  <ListItem 
                    key={item.id || item.equipment_id || index}
                    button
                    onClick={() => handleSelectFallbackItem(item)}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {item.description || item.equipment_name || 'Equipment Item'}
                          </Typography>
                          <Chip
                            label={getStatusDisplay(item.status).label}
                            sx={getStatusChipSx(item.status, getStatusChipVariant(item.status))}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>ID:</strong> {item.equipment_id || item.id} • 
                            <strong>Make/Model:</strong> {item.manufacturer} {item.model}
                          </Typography>
                          {item.serial_number && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Serial:</strong> {item.serial_number}
                            </Typography>
                          )}
                          {item.current_job_id && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Assigned to:</strong> Job {item.current_job_id}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReprintQRCode(item);
                        }}
                      >
                        Reprint QR
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSelectFallbackItem(item)}
                      >
                        Select
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFallbackLookup(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleFallbackSearch}
            disabled={!fallbackData.manufacturer.trim() || !fallbackData.model.trim() || isSearching}
            startIcon={isSearching ? <CircularProgress size={16} /> : <SearchIcon />}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Unplanned Equipment Dialog */}
      <UnplannedEquipmentDialog
        open={showUnplannedDialog}
        onClose={handleUnplannedEquipmentCancel}
        equipment={unplannedEquipmentData?.equipment}
        job={unplannedEquipmentData?.job}
        onConfirm={handleUnplannedEquipmentConfirm}
        onCancel={handleUnplannedEquipmentCancel}
        loading={isProcessing}
      />
    </Box>
  );
}